<template>
  <div
    v-if="product"
    class="size-selector"
    :class="{
      [$class('active')]: anyActive || variant?.id,
      ['size-selector--select']: showSelectDropdown,
    }"
  >
    <div
      v-if="anyActive && hoveredVariant"
      class="size-selector__selected-variant-message"
    >
      <span
        v-if="hoveredVariant.available && hoveredVariant.inventory < lowAvailabilityThreshold"
        v-text="$string('product.cta.low_availability')"
      />
      <span
        v-if="!hoveredVariant.available"
        v-text="$string('product.cta.notify_me_mobile')"
      />
    </div>
    <div
      v-if="!comingSoonProduct"
      class="size-selector__wrapper"
      :class="{
        ['form-group']: showSelectDropdown,
      }"
      @mouseenter="() => anyActive = true"
      @mouseleave="handleMouseLeave"
    >
      <template v-if="showSelectDropdown">
        <select
          class="size-selector__select select--large"
          @change="handleClick(sortedSizes[$event.target.selectedIndex - 1])"
        >
          <option
            value="-1"
            v-text="$string('product.form.select_label')"
          />
          <option
            v-for="(variantObject, index) of sortedSizes"
            :key="index"
            :selected="variantObject.id === variant?.id"
            @click="handleClick(variantObject)"
            v-text="sizeSelectorOptionText(variantObject, $string('product.cta.notify_me'))"
          />
        </select>
      </template>

      <template
        v-for="(variantObject, index) of sortedSizes"
        v-else
        :key="index"
      >
        <button
          class="
            text-desktop-small-uc size-selector__button
          "
          :class="{
            [$class('active')]: variantObject.id === variant?.id,
            ['size-selector__button--oos']: !variantObject.available,
            ['size-selector__button--single-size']: product?.variants?.length === 1,
          }"
          type="button"
          @click="handleClick(variantObject)"
          @mouseenter="() => handleSizeHover(variantObject)"
          v-text="variantObject.title"
        />
      </template>
    </div>

    <!-- Size Selector Action -->
    <div
      v-if="isSizeGuideAvailable || sizeGuideFitLabel"
      class="size-selector__size-guide"
    >
      <button
        v-if="isSizeGuideAvailable"
        class="
          text-desktop-small
          size-selector__action-button
          size-selector__size-guide-button
        "
        type="button"
        @click="openOverlay({
          component: 'size-guide',
          ignoreDismissed: true,
          namespace: 'sizeGuide',
        }), () => handleClick(null, true)"
        @mouseenter="sizeGuideHover = true"
        @mouseleave="sizeGuideHover = false"
      >
        <span
          v-text="sizeGuideHover ? sizeGuideButtonLabel : sizeGuideFitLabel"
        />
        <icon-chevron-right-special v-if="isSizeGuideAvailable" />
      </button>
      <div
        v-else-if="sizeGuideFitLabel"
        class="text-desktop-small size-selector__fit-label"
      >
        <span v-text="sizeGuideFitLabel" />
        <icon-chevron-right-special v-if="isSizeGuideAvailable" />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Size selector (size-selector)
 * -----------------------------------------------------------------------------
 * Enables the selection of a product size on pdp.
 * @param {Boolean} isSizeGuideAvailable - Boolean indicating is size guide
 * has content to display.
 * @param {Object} product - Shopify product object.
 * @param {Boolean} showSizeGuide - Indicates if the size guide is displayed.
 * @param {String} sizeGuideFitLabel - Fit label for the size guide.
 * @param {Array} sizeOrder - Array of size strings.
 * @param {Boolean, Object} variant - Shopify variant object.
 *
 */

import { mapActions } from 'vuex'

import IconChevronRightSpecial from '~icons/direction/chevron/right-special.svg'

export default {
  name: 'SizeSelector',
  components: {
    IconChevronRightSpecial,
  },
  props: {
    isSizeGuideAvailable: {
      type: Boolean,
      default: false,
    },

    product: {
      type: [Boolean, Object],
      default: false,
    },

    showSizeGuide: {
      type: Boolean,
      default: false,
    },

    sizeGuideFitLabel: {
      type: [Boolean, String],
      default: () => false,
    },

    sizeOrder: {
      type: Array,
      default: () => [],
    },

    variant: {
      type: [Boolean, Object],
      default: false,
    },

    lowAvailabilityThreshold: {
      type: Number,
      default: 3,
    },
  },

  emits: {
    'coming-soon-hover': (payload) => typeof payload === 'boolean',
    'size-selector-action': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      anyActive: false,
      comingSoonHover: false,
      displayAction: 'sizeGuide',
      selected: null,
      sizeGuideHover: false,
      hoveredVariant: null,
    }
  },

  computed: {

    /**
     * Returns whether a product is coming soon or not.
     * - If product is preorder, coming soon is always false
     * @returns {Boolean}
     */
    comingSoonProduct() {
      const preOrderProduct = this.product?.tags?.find(
        tag => tag.toLowerCase().includes('preorder'),
      )

      if (preOrderProduct) {
        return false
      }

      return Boolean(this.product?.tags?.find(
        tag => tag.toLowerCase().includes('coming-soon'),
      ))
    },

    /**
     * Returns whether a product should display a select dropdown or not.
     * @returns {Boolean}
     */
    showSelectDropdown() {
      return this.product?.type.toLowerCase() === 'denim' || this.product?.type.toLowerCase() === 'jeans'
    },

    /**
     * Returns a suitable button label for the size guide button.
     * @returns {String}
     */
    sizeGuideButtonLabel() {
      return this.showSizeGuide
        ? this.$string('product.form.size_guide_close')
        : this.$string('product.form.size_guide')
    },

    /**
     * Computes sorted product variants.
     * @returns {Array}
     */
    sortedSizes() {
      if (!this.product?.variants?.length) {
        return []
      }

      const newProduct = JSON.parse(JSON.stringify(this.product))

      if (!Number(newProduct.variants[0].title)) {
        return newProduct.variants.sort(
          (a, b) => {
            return this.sizeOrder.indexOf(a.title) -
              this.sizeOrder.indexOf(b.title)
          },
        )
      }

      return newProduct.variants.sort((a, b) => a.title - b.title)
    },
  },

  watch: {

    /**
     * When the product updates we need to reset this component.
     */
    product() {
      this.resetComponent()
    },

    /**
     * When the variant updates we need to show the equivalent product variant
     * option as selected.
     */
    variant: {
      immediate: true,
      handler(variant) {
        if (!variant) {
          return
        }
        this.handleSizeHover(variant)
      },
    },

    /**
     * Emit an event when coming soon message is hovered.
     */
    comingSoonHover(value) {
      this.$emit('coming-soon-hover', value)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Resets the component to its initial state.
     * - Called by parent AFTER add-to-cart.
     */
    resetComponent() {
      this.displayAction = 'sizeGuide'
      this.anyActive = false
      this.hoveredVariant = null
      this.selected = null
    },

    /**
     * Handles click events on a variant selector.
     * @param {Object} variant - Variant object.
     * @param {Boolean} openSizeGuide - Toggles whether to open/close the size
     * guide.
     */
    handleClick(variant, openSizeGuide) {
      const event = {
        action: null,
        variant,
      }

      if (variant?.available) {
        event.action = 'variant-updated'
      } else if (openSizeGuide) {
        event.action = 'open-size-guide'
      } else {
        event.action = 'notify-me'
      }

      this.$emit('size-selector-action', event)
    },

    sizeSelectorOptionText(variantObject, string) {
      return variantObject.title +
        (variantObject.available ? '' : ` - ${string}`)
    },

    /**
     * Handles hovering over a variant/size.
     * @param {Object} variant - Variant object.
     */
    handleSizeHover(variant) {
      this.hoveredVariant = variant

      if (this.variant && this.variant.available) {
        this.displayAction = 'sizeGuide'
        return
      }

      this.displayAction = 'notify'
    },

    /**
     * Handles hovering over coming soon text.
     */
    handleComingSoonHover(value) {
      this.$emit('coming-soon-hover', value)
    },

    /**
     * Handles mouse leave event.
     */
    handleMouseLeave() {
      this.anyActive = false
      this.hoveredVariant = null

      if (!this.selected) {
        this.displayAction = 'sizeGuide'
        return
      }

      if (this.selected && this.selected.available) {
        this.displayAction = 'sizeGuide'
        return
      }

      this.displayAction = 'notify'
    },
  },
}
</script>

<style lang="scss">
@import './size-selector';
</style>
